import {AuthBody, Message, PingBody, RequestType, SubscribeBody, UnsubscribeBody} from './web.socket.request.types';
import {v4 as uuidv4} from 'uuid';
import { AuthResponseBody, ResponseMessage, ResponseStatus, ResponseType } from './web.socket.response.types';
import { WebSocketSubscription } from './web.socket.subscription';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { MsalInterceptor } from '../../../msal/msal.interceptor';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { WebSocketLoggerService } from './web.socket.logger.service';
import { WebSocketConfigService } from './web.socket.config.service';
import { map, take } from 'rxjs/operators';
import { IWebSocketInstance, IWebSocketSubscription } from './web.socket.instance.interface';

export class WebSocketInstanceService implements IWebSocketInstance {
  private webSocket: WebSocket | undefined;
  private readonly clientId: string = uuidv4();
  public isConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly subscriptions: Map<string, WebSocketSubscription> = new Map<string, WebSocketSubscription>();
  public onMessageReceived: Subject<ResponseMessage | undefined> = new Subject<ResponseMessage | undefined>();
  private readonly onAuthReceived: Subject<ResponseMessage | undefined> = new Subject<ResponseMessage | undefined>();
  private authExpireTS: Date;
  private readonly reconnectDelay = 1000;
  private hbInterval: ReturnType<typeof setInterval> | undefined;
  private pingTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly url: string, private readonly msalInterceptor: MsalInterceptor,
              private readonly logger: WebSocketLoggerService, private readonly config: WebSocketConfigService) {
    this.connect();
  }

  private getToken(): Observable<string> {
    const fakeRequest = new HttpRequest<any>('GET', 'http://fake.local');
    return new Observable<string>(observer => {
      // Create a fake HTTP handler that captures the Authorization header
      const fakeHandler: HttpHandler = {
        handle: (req: HttpRequest<any>): Observable<HttpEvent<any>> => {
          // Extract the Authorization header from the intercepted request
          const authHeader = req.headers.get('Authorization');
          observer.next(authHeader);
          observer.complete();
          return of({} as HttpEvent<any>);
        }
      };
      // Use MSAL interceptor to get the token
      this.msalInterceptor.intercept(fakeRequest, fakeHandler).subscribe({
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  private processWsMessage(data: any): void {
    try {
      const wsMsg = JSON.parse(data) as ResponseMessage;
      if (wsMsg?.header?.type === ResponseType.AUTH_RESPONSE) {
        this.onAuthReceived.next(wsMsg);
      } else if (wsMsg?.header?.type === ResponseType.PING_RESPONSE) {
        clearTimeout(this.pingTimeout);
      } else if (this.isConnected.value) {
        this.onMessageReceived.next(wsMsg);
      }
    } catch (err) {
      this.logger.error('WebSocketInstanceService', 'Failed to parse WS message:', err, data);
    }
  }

  private authenticate(): Observable<boolean> {
    if (this.authExpireTS && new Date() < this.authExpireTS) {
      return of(true);
    }
    return new Observable<boolean>(observer => {
      this.getToken().subscribe(token => {
        this.sendRaw(RequestType.AUTH, {token});
        this.onAuthReceived.pipe(
          take(1)
        ).subscribe(wsMsg => {
          let isAuthValid = false;
          if (wsMsg?.header?.status === ResponseStatus.SUCCESS) {
            this.authExpireTS = (wsMsg.body as AuthResponseBody).expireTs;
            isAuthValid = true;
          } else if (this.isConnected.value) {
            this.authExpireTS = void 0;
          }
          observer.next(isAuthValid);
          observer.complete();
        });
      });
    });
  }

  private initConnection(): void {
    // first step - authenticate
    this.logger.log('WebSocketInstanceService', `Connected, initializing connection...`);
    this.authenticate().subscribe(isAuthValid => {
      if (isAuthValid) {
        // active connection
        this.activateConnection();
        // restart all previously established subscriptions
        this.subscriptions.forEach((subscription) => {
          subscription.reconnect();
        });
      } else {
        // failed to authenticate - disconnect
        this.disconnect(true);
      }
    });
  }

  private connect(): void {
    this.logger.log('WebSocketInstanceService', `Connecting to ${this.url}`);
    this.webSocket = new WebSocket(this.url, ['prism']);
    this.webSocket.addEventListener('message', (msg) => this.processWsMessage(msg.data));
    this.webSocket.addEventListener('error', (ev) => { this.disconnect(); });
    this.webSocket.addEventListener('close', (ev) => { this.reconnect(); });
    this.webSocket.addEventListener('open', (ev) => { this.initConnection(); });
  }

  private reconnect(): void {
    this.disconnect(false);
    setTimeout(() => {
      this.connect();
    }, this.reconnectDelay);
  }

  private disconnect(destroySub: boolean = false): void {
    // on disconnect destroy all subscriptions
    if (destroySub) {
      this.subscriptions.forEach((subscription) => {
        subscription.destroy();
      });
    }
    // and close web socket connection
    if (this.webSocket?.readyState === this.webSocket?.OPEN) {
      this.webSocket?.close();
    }
    // cancel heartbeat
    if (this.hbInterval) {
      clearInterval(this.hbInterval);
    }
    this.isConnected.next(false);
  }

  private activateConnection(): void {
    setTimeout(() => {
      // flag as connected in the next tick to avoid resubscription (this.subscriptions.forEach -> reconnect())
      this.isConnected.next(true);
    });
    this.hbInterval = setInterval(() => {
      this.sendRaw(RequestType.PING, {pingTz: new Date()});
      // Set a timer to wait for pong. If not received, close & reconnect.
      this.pingTimeout = setTimeout(() => {
        this.logger.warn('WebSocketInstanceService', `No pong received, trying to reconnect...`);
        this.reconnect();
      }, this.config.pingTimeout);
    }, this.config.pingInterval);
  }

  subscribe(channel: string): IWebSocketSubscription {
    if (!this.isConnected.value) {
      throw Error(`not connected to web socket, can't establish subscription`);
    }
    let subscription: WebSocketSubscription;
    if (this.subscriptions.has(channel)) {
      subscription = this.subscriptions.get(channel);
    } else {
      subscription = new WebSocketSubscription(channel, this);
      subscription.onDestroy.subscribe(channel => this.subscriptions.delete(channel));
      this.subscriptions.set(channel, subscription);
    }

    return subscription;
  }

  public send(type: RequestType, body: AuthBody | SubscribeBody | UnsubscribeBody | PingBody): Observable<string> {
    return this.authenticate().pipe(
      take(1),
      map(isAuthValid => {
        if (isAuthValid) {
          return this.sendRaw(type, body);
        } else {
          throw Error(`not authenticated to web socket, can't send message`);
        }
      })
    );
  }

  private sendRaw(type: RequestType, body: AuthBody | SubscribeBody | UnsubscribeBody | PingBody): string {
    if (this.webSocket?.readyState !== WebSocket.OPEN) {
      return '';
    }
    const messageId = uuidv4();
    const message: Message = {
      header: {
        type,
        clientId: this.clientId,
        messageId,
      },
      body,
    };
    this.webSocket?.send(JSON.stringify(message));
    return messageId;
  }
}
